import { fieldOptions } from './field-option.ts'
import type { SearchLocale } from '../../i18n/install.ts'
import type { FieldOptionItem } from './field-option.ts'

export type { FieldOptionItem } from './field-option.ts'
export { fieldOptions } from './field-option.ts'

export type AttrTypeSuffix =
  'string' | 'int' | 'float' | 'date' | 'datetime' | 'bool'

type AttrLocale = SearchLocale

const COMPARISON_OPERATORS = ['>', '<', '>=', '<=', '=', '!=']

/**
 * Custom document attributes as indexed by the search API.
 *
 * - `name` is the path after `attr.` (may contain dots),
 *   e.g. `doc.InvoiceDate` → API key `attr.doc.InvoiceDate.date`
 * - String attributes have no type suffix: `attr.doc.VendorName` (not `attr.doc.VendorName.string`)
 * - Display labels live in i18n (`deep_search.attr.<name>`); optional `labels` override the catalog
 */
export interface CustomAttrDefinition {
  /** Indexed attribute path (without `attr.` prefix / type suffix). */
  name: string
  suffix: AttrTypeSuffix
  /** Optional host/package override for localized display labels. */
  labels?: Partial<Record<AttrLocale, string>>
  values?: FieldOptionItem[]
  /** Optional display labels for enum-like values. */
  valueLabels?: Record<string, Partial<Record<AttrLocale, string>> | string>
}

/** Preserve literal `name` / `suffix` unions from the definitions array. */
function defineCustomAttrs<const T extends readonly CustomAttrDefinition[]>(
  definitions: T,
): T {
  return definitions
}

/** API query key template for an attribute definition. */
export type AttrFieldKey<
  Name extends string,
  Suffix extends AttrTypeSuffix,
> = Suffix extends 'string' ? `attr.${Name}` : `attr.${Name}.${Suffix}`

export type CustomAttrFieldIdFromDef<T> = T extends {
  name: infer Name extends string
  suffix: infer Suffix extends AttrTypeSuffix
}
  ? AttrFieldKey<Name, Suffix>
  : never

/** Explicit type suffixes that appear in the API key (string is the default — no suffix). */
const TYPED_ATTR_SUFFIX_PATTERN = 'int|float|date|datetime|bool'

/**
 * Document attributes (attr.*). These are NOT built-in qualifiers —
 * the query must use the full `attr.<name>` key (plus an optional type suffix).
 * Labels: `deep_search.attr.<name>` in engine i18n locales.
 */
export const CUSTOM_ATTR_DEFINITIONS = defineCustomAttrs([
  { name: 'Assignee', suffix: 'string' },
  { name: 'doc.InvoiceDate', suffix: 'date' },
  { name: 'doc.DocumentTitle', suffix: 'string' },
  { name: 'doc.DocumentType', suffix: 'string' },
  { name: 'doc.DocumentLanguage', suffix: 'string' },
  { name: 'doc.Currency', suffix: 'string' },
  { name: 'doc.TotalInclVat', suffix: 'float' },
  { name: 'doc.InvoicePeriodFrom', suffix: 'date' },
  { name: 'doc.InvoicePeriodTo', suffix: 'date' },
  { name: 'doc.DueDate', suffix: 'date' },
  { name: 'doc.InvoiceNumber', suffix: 'string' },
  { name: 'doc.PurchaseOrderNumber', suffix: 'string' },
  { name: 'doc.VendorName', suffix: 'string' },
  { name: 'doc.VendorCity', suffix: 'string' },
  { name: 'doc.VendorZip', suffix: 'string' },
  { name: 'doc.VendorCountry', suffix: 'string' },
  { name: 'doc.CustomerName', suffix: 'string' },
  { name: 'doc.CustomerCity', suffix: 'string' },
  { name: 'doc.CustomerZip', suffix: 'string' },
  { name: 'doc.CustomerCountry', suffix: 'string' },
])

/** Attribute path after `attr.` (e.g. `doc.Currency`, `doc.VendorName`). */
export type SearchAttributeName =
  (typeof CUSTOM_ATTR_DEFINITIONS)[number]['name']

/** Full query field id for a known custom attribute (e.g. `attr.doc.Currency`). */
export type CustomAttrFieldId = CustomAttrFieldIdFromDef<
  (typeof CUSTOM_ATTR_DEFINITIONS)[number]
>

/** API query key: `attr.<name>` for strings, `attr.<name>.<suffix>` for typed attrs. */
export function buildAttrFieldKey<
  Name extends string,
  Suffix extends AttrTypeSuffix,
>(name: Name, suffix: Suffix): AttrFieldKey<Name, Suffix> {
  if (suffix === 'string') {
    return `attr.${name}` as AttrFieldKey<Name, Suffix>
  }
  return `attr.${name}.${suffix}` as AttrFieldKey<Name, Suffix>
}

/**
 * Parse an API attribute key. Supports nested names and default string (no suffix):
 * `attr.doc.InvoiceDate.date` → `{ name: 'doc.InvoiceDate', suffix: 'date' }`
 * `attr.doc.VendorName` → `{ name: 'doc.VendorName', suffix: 'string' }`
 */
export function parseAttrFieldKey(
  key: string,
): { name: string; suffix: AttrTypeSuffix } | null {
  const trimmed = key.trim()
  if (!trimmed.toLowerCase().startsWith('attr.')) {
    return null
  }

  const rest = trimmed.slice('attr.'.length)
  if (
    !rest ||
    rest.startsWith('.') ||
    rest.endsWith('.') ||
    rest.includes('..')
  ) {
    return null
  }

  const typedMatch = new RegExp(
    String.raw`^(.+)\.(${TYPED_ATTR_SUFFIX_PATTERN})$`,
    'i',
  ).exec(rest)
  if (typedMatch) {
    return {
      name: typedMatch[1],
      suffix: typedMatch[2].toLowerCase() as AttrTypeSuffix,
    }
  }

  return {
    name: rest,
    suffix: 'string',
  }
}

/** i18n key for a custom attribute display label. */
export function attrLabelI18nKey(name: string): string {
  return `deep_search.attr.${name}`
}

/** Fallback display label from an attribute path, e.g. `doc.InvoiceDate` → `Invoice date`. */
export function humanizeAttrName(name: string): string {
  const leaf = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : name
  const spaced = leaf
    .replaceAll(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replaceAll(/[_-]+/g, ' ')
    .trim()
  if (!spaced) {
    return name
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

export function isAttrFieldKey(key: string): boolean {
  return key.toLowerCase().startsWith('attr.')
}

export function attrSuffixToFieldType(suffix: AttrTypeSuffix): string {
  switch (suffix) {
    case 'string': {
      return 'String'
    }
    case 'int': {
      return 'int'
    }
    case 'float': {
      return 'Amount'
    }
    case 'date': {
      return 'Date'
    }
    case 'datetime': {
      return 'Datetime'
    }
    case 'bool': {
      return 'bool'
    }
  }
}

export function getOperatorsForAttrSuffix(
  suffix: AttrTypeSuffix,
): string[] | undefined {
  switch (suffix) {
    case 'int':
    case 'float':
    case 'date':
    case 'datetime': {
      return COMPARISON_OPERATORS
    }
    default: {
      return undefined
    }
  }
}

export function getDefaultValuesForAttrSuffix(
  suffix: AttrTypeSuffix,
): FieldOptionItem[] {
  if (suffix === 'bool') {
    return fieldOptions('true', 'false')
  }
  return []
}

export function fieldSupportsRangeSyntax(
  suffix: AttrTypeSuffix | undefined,
  fieldType: string,
): boolean {
  if (suffix) {
    return ['int', 'float', 'date', 'datetime'].includes(suffix)
  }
  return ['int', 'Amount', 'Date', 'Datetime'].includes(fieldType)
}

export function fieldSupportsExistsWildcard(
  suffix: AttrTypeSuffix | undefined,
  fieldType: string,
  hasOperators: boolean,
): boolean {
  void hasOperators
  if (suffix) {
    return true
  }
  return [
    'String',
    'DocumentType',
    'UUID',
    'i18n',
    'int',
    'Amount',
    'Date',
    'Datetime',
    'bool',
  ].includes(fieldType)
}

const CUSTOM_ATTR_BY_KEY = new Map(
  CUSTOM_ATTR_DEFINITIONS.map((definition) => [
    buildAttrFieldKey(definition.name, definition.suffix).toLowerCase(),
    definition,
  ]),
)

export function resolveCustomAttrDefinition(
  key: string,
): CustomAttrDefinition | null {
  const known = CUSTOM_ATTR_BY_KEY.get(key.toLowerCase())
  if (known) {
    return known
  }

  const parsed = parseAttrFieldKey(key)
  if (!parsed) {
    return null
  }

  return {
    name: parsed.name,
    suffix: parsed.suffix,
    values: getDefaultValuesForAttrSuffix(parsed.suffix),
  }
}
